import { httpClient } from '../httpClient';

// Spec 051 US4 (T048) — shared invalidation keys for inventory objects that
// are referenced by ROPA records (ProcessingEntity, DataSource). Any module
// that mutates these objects must invalidate these keys so ROPA detail views
// reflect the updated name / status without a manual page reload (FR-051-27,
// SC-006, XVII.4 — one canonical declaration, no dup fetch).
export const ROPA_INVENTORY_QUERY_KEYS = {
  processingEntities: ['ropa-processing-entities'],
  records: ['ropa', 'records'],
};

// 051 FR-051-06/10 — map the form-shape ROPA payload to the API write shape:
//   • recipients → [{ entityId, role, disclosureType }
//                    | { customLabel, role, disclosureType }]   (FR-051-06)
//   • transfer   → { sharingScope, transferMechanism, transferLegalBasis,
//                    destinations, narrative }                   (FR-051-10)
// Every other field passes through untouched. The transform is defensive: it
// only fires when the data carries the 051 form shapes, so the duplicate /
// archive paths (which feed display-shaped or partial records) degrade
// gracefully instead of mangling unrelated payloads.
export function buildRopaWritePayload(data) {
  if (!data || typeof data !== 'object') return data;
  const payload = { ...data };

  if (Array.isArray(data.recipients)) {
    payload.recipients = data.recipients
      .map((entry) => {
        if (!entry) return null;
        if (typeof entry === 'string') {
          return entry.trim()
            ? { customLabel: entry.trim(), role: 'controller', disclosureType: 'authorised' }
            : null;
        }
        const role = entry.role || 'controller';
        const disclosureType = entry.disclosureType || 'authorised';
        const isCustom = entry.isCustom != null
          ? Boolean(entry.isCustom)
          : (entry.kind ? entry.kind !== 'entity' : !(entry.entityId ?? entry.id));
        if (isCustom) {
          const customLabel = entry.customLabel || entry.label || '';
          return customLabel ? { customLabel, role, disclosureType } : null;
        }
        const entityId = entry.entityId ?? entry.id ?? null;
        return entityId != null ? { entityId, role, disclosureType } : null;
      })
      .filter(Boolean);
  }

  if (data.transfer && typeof data.transfer === 'object' && !Array.isArray(data.transfer)) {
    const tr = data.transfer;
    payload.transfer = {
      sharingScope: data.sharingScope || 'none',
      transferMechanism: tr.transferMechanism || '',
      transferLegalBasis: tr.transferLegalBasis || '',
      destinations: Array.isArray(tr.destinations) ? tr.destinations : [],
      narrative: tr.narrative || '',
    };
    // Keep the legacy free-text column populated for dual-read backends.
    if (tr.narrative && !payload.dataTransfer) payload.dataTransfer = tr.narrative;
  }

  return payload;
}

export const ropaApi = {
  getAll: (params) =>
    httpClient.get('/ropa/records', { params }),

  getById: (id) =>
    httpClient.get(`/ropa/records/${id}`),

  create: (data) =>
    httpClient.post('/ropa/records', buildRopaWritePayload(data)),

  update: (id, data, options) =>
    httpClient.put(`/ropa/records/${id}`, buildRopaWritePayload(data), options),

  delete: (id) =>
    httpClient.delete(`/ropa/records/${id}`),

  bulkDelete: (ids) =>
    httpClient.post('/ropa/records/bulk-delete', { ids }),

  exportPdf: (id, { language } = {}) =>
    httpClient.post(
      `/ropa/records/${id}/export-pdf`,
      language ? { language } : {},
      { responseType: 'blob' },
    ),

  exportAll: (data) =>
    httpClient.post('/ropa/export-all', data, { responseType: 'blob' }),

  // QA #24 / Plan §5.3 — Transfer Risk Assessment bilingual PDF.
  // Backend route: GET /api/ropa/export/tra-pdf?data_source_id=N (optional).
  // Returns application/pdf with X-PrivacyGuard-Payload-Hash for
  // chain-of-custody.
  exportTraPdf: ({ dataSourceId } = {}) =>
    httpClient.get('/ropa/export/tra-pdf', {
      params: dataSourceId ? { data_source_id: dataSourceId } : {},
      responseType: 'blob',
    }),

  dismissMergeReview: (id) =>
    httpClient.post(`/ropa/records/${id}/dismiss-merge-review`),

  getStats: () =>
    httpClient.get('/ropa/stats'),

  // 021-ropa-module Phase 4 — lifecycle + version history (T061, T062, T063).
  // P-CONTRACT: backend lifecycle.py flags transitions to `approved`/`active`
  // as privileged and calls verify_step_up_password(); the caller MUST pass
  // a `password` for those transitions or the request will 401 with
  // code=step_up_required. Non-privileged transitions ignore the field.
  transitionLifecycle: (id, toState, { reason, password } = {}) =>
    httpClient.post(`/ropa/records/${id}/lifecycle`, {
      to_state: toState,
      reason: reason || null,
      ...(password ? { password } : {}),
    }),

  getVersions: (id, params) =>
    httpClient.get(`/ropa/records/${id}/versions`, { params }),

  // 051-ropa-creation-flow US7 (T067) — structured approval/audit ledger.
  // GET /api/ropa/records/:id/approvals returns the ROPAApproval rows
  // (newest first) with an identifiable `approverName` resolved server-side
  // so the workflow panel can render who decided each transition (FR-051-34/35).
  getApprovals: (id, params) =>
    httpClient.get(`/ropa/records/${id}/approvals`, { params }),

  getVersionsDiff: (id, fromVersion, toVersion) =>
    httpClient.get(`/ropa/records/${id}/versions/diff`, {
      params: { from: fromVersion, to: toVersion },
    }),

  // T097 — field-override lock CRUD.
  createFieldLock: (id, { field, reason, lockedValueSnapshot }) =>
    httpClient.post(`/ropa/records/${id}/locks`, {
      field,
      reason: reason || null,
      locked_value_snapshot: lockedValueSnapshot || null,
    }),

  removeFieldLock: (id, field) =>
    httpClient.delete(`/ropa/records/${id}/locks/${encodeURIComponent(field)}`),

  // DPO feedback (2026-05-21) — local-data field suggestions. Whitelist
  // enforced server-side; non-whitelisted fields return 400.
  // Bundle 1 ships the route and UI; Bundle 5 implements the engine.
  suggestField: (id, field) =>
    httpClient
      .get(`/ropa/records/${id}/fields/${encodeURIComponent(field)}/suggest`)
      .then((res) => res?.data?.data ?? res?.data ?? { suggestions: [] }),

  // T113 — PDPL citation metadata.
  getCitations: ({ asOfDate, fields } = {}) =>
    httpClient.get('/ropa/citations', {
      params: {
        asOfDate: asOfDate || undefined,
        fields: Array.isArray(fields) && fields.length ? fields.join(',') : undefined,
      },
    }),

  // T114/T118 — linked compliance policies.
  getLinkedPolicies: (id) =>
    httpClient.get(`/ropa/records/${id}/linked-policies`),

  // T128/T130 — industry templates.
  // 051 US6 — role param added for gallery controller/processor filtering.
  getTemplates: ({ sector, role, active } = {}) =>
    httpClient.get('/ropa/templates', {
      params: {
        sector: sector || undefined,
        role: role || undefined,
        active: active === false ? 'false' : undefined,
      },
    }),

  createFromTemplate: ({ slug, overrides }) =>
    httpClient.post('/ropa/records/from-template', {
      slug,
      overrides: overrides || {},
    }),

  // Spec 051 US5 (T053) — auto-populate a draft from a linked Processing
  // Activity. Returns { draft, autoFilled } (NOT persisted); the form opens
  // pre-filled with the resolved DataSource / categories / location and the
  // per-field provenance markers (FR-051-29/30). 404 when the activity is
  // unknown — the caller falls back to a blank manual draft.
  draftFromActivity: (activityId) =>
    httpClient
      .post('/ropa/records/draft-from-activity', { activityId })
      .then((res) => res?.data?.data ?? res?.data ?? null),

  // T134 — benchmarking engine.
  getBenchmark: ({ sector } = {}) =>
    httpClient.get('/ropa/benchmark', {
      params: { sector: sector || 'auto' },
    }),

  // T139 — grouping dimensions.
  getGroupCounts: ({ groupBy } = {}) =>
    httpClient.get('/ropa/records/group-counts', {
      params: { groupBy: groupBy || 'businessUnit' },
    }),

  // T147 — reactflow diagram payload.
  getFlowDiagram: (id) =>
    httpClient.get(`/ropa/records/${id}/flow-diagram`),

  // T156 — schema-change alert lifecycle.
  getSchemaChangeAlerts: ({ includeAcknowledged } = {}) =>
    httpClient.get('/ropa/schema-change-alerts', {
      params: {
        includeAcknowledged: includeAcknowledged ? '1' : undefined,
      },
    }),

  acknowledgeSchemaChangeAlert: (alertId) =>
    httpClient.post(
      `/ropa/schema-change-alerts/${alertId}/acknowledge`,
      {},
    ),

  // T164 — retention severity breakdown (wraps Phase-6 /retention/severity-breakdown).
  getRetentionSeverity: () =>
    httpClient.get('/ropa/retention/severity-breakdown'),

  runRetentionScan: () =>
    httpClient.post('/ropa/retention/run-scan', {}),

  // T169 — Command Center aggregate payload.
  getCommandCenter: ({ timeRange } = {}) =>
    httpClient.get('/ropa/command-center', {
      params: timeRange && timeRange !== 'all' ? { time_range: timeRange } : undefined,
    }),

  // T194 — DSR subject lookup (J.1, US-15). Returns the processing
  // activities covering the given subject id — fan-out capped at 50.
  getBySubject: (subjectId) =>
    httpClient.get(`/ropa/by-subject/${encodeURIComponent(subjectId)}`),

  /**
   * Options feed for the form's `processingPurposeId` FK picker.
   *
   * Every `id` here is a real `ProcessingPurpose.id` — the only value that
   * may be written into `ropa_records.processing_purpose_id`. Do NOT swap
   * this for `purposeCurationApi.listProcessingPurposes()`: despite its
   * name that endpoint's `id` is a `ROPARecord.id`, and binding it to this
   * FK silently links a different purpose than the option named.
   *
   * @param {object}  [params]
   * @param {string}  [params.search] substring match on the purpose name
   * @param {number}  [params.limit]  page size (default 200, max 500)
   */
  listProcessingPurposeOptions: (params) =>
    httpClient.get('/ropa/processing-purposes', { params }),

  // Spec 026 — FR-026-11 aggregate safeguard rollup for a ROPA record.
  getSafeguardStatus: (id) =>
    httpClient.get(`/ropa/records/${id}/safeguard-status`),

  // Spec 027 P5 — FR-027-09: Processor DPO declarations.
  getProcessorDPO: (id) =>
    httpClient.get(`/ropa/records/${id}/processor-dpo`),

  createProcessorDPO: (id, data) =>
    httpClient.post(`/ropa/records/${id}/processor-dpo`, data),

  verifyProcessorDPO: (id, declId) =>
    httpClient.patch(`/ropa/records/${id}/processor-dpo/${declId}/verify`, {}),

  // Spec 027 P6 — FR-027-06: DPO Watchlist.
  listWatchlist: () =>
    httpClient.get('/ropa/watchlist'),

  // GH #191 — the backend _PIN_SCHEMA (modules/ropa/routes_watchlist.py) declares
  // `ropaRecordId` (camelCase, type int). Posting snake_case failed validation
  // with a 400 on every pin click, so the payload key must stay camelCase.
  pinRopa: (ropaRecordId) =>
    httpClient.post('/ropa/watchlist', { ropaRecordId }),

  unpinRopa: (watchlistItemId) =>
    httpClient.delete(`/ropa/watchlist/${watchlistItemId}`),

  acknowledgeWatchlistItem: (itemId) =>
    httpClient.post(`/ropa/watchlist/${itemId}/acknowledge`, {}),

  // Spec 028 FR-028-02 — DPO purge queue (retention_eligible_at <= now).
  listRetentionEligible: () =>
    httpClient.get('/ropa/records/retention-eligible'),

  purgeRecord: (id) =>
    httpClient.delete(`/ropa/records/${id}/purge`),
};
